import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { captureElementJpeg } from '../utils/captureVisibleFrame';
import { openCleanFrame } from '../utils/youtubeCleanFrame';
import { attachYouTubeWidget } from '../utils/youtubeWidget';

function embedSrc(embedUrl) {
  const url = new URL(embedUrl);
  url.searchParams.set('enablejsapi', '1');
  if (typeof window !== 'undefined' && window.location?.origin) {
    url.searchParams.set('origin', window.location.origin);
  }
  return url.toString();
}

const YoutubeVideoModal = ({ video, extensionId, onClose, onMoment }) => {
  const open = Boolean(video?.videoId && video?.embedUrl);
  const title = video?.title || 'YouTube video';
  const frameRef = useRef(null);
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const onMomentRef = useRef(onMoment);
  const extensionIdRef = useRef(extensionId);
  const captureJobRef = useRef(null);
  const lastImageKeyRef = useRef('');
  const [capturing, setCapturing] = useState(false);
  onMomentRef.current = onMoment;
  extensionIdRef.current = extensionId;
  const src = useMemo(
    () => (video?.embedUrl ? embedSrc(video.embedUrl) : ''),
    [video?.embedUrl],
  );

  const captureMoment = knownSeconds => {
    if (captureJobRef.current) return captureJobRef.current;
    let resolveJob = () => {};
    const job = new Promise(resolve => {
      resolveJob = resolve;
    });
    captureJobRef.current = job;
    (async () => {
      try {
        let seconds = Number(knownSeconds);
        if (!(seconds >= 1)) {
          seconds = Number(await widgetRef.current?.getCurrentTime?.());
        }
        if (!(seconds >= 1)) return;
        const key = `${video?.videoId || ''}:${Math.floor(seconds)}`;
        if (lastImageKeyRef.current === key) return;
        onMomentRef.current?.(seconds);

        let imageDataUrl = '';
        const hostId = extensionIdRef.current;
        if (hostId && frameRef.current && video?.videoId) {
          setCapturing(true);
          const host = document.createElement('div');
          host.className = 'board-youtube-clean-capture';
          frameRef.current.appendChild(host);
          const clean = openCleanFrame(host, video.videoId, seconds);
          try {
            await clean.ready;
            const rect = host.getBoundingClientRect();
            imageDataUrl = await captureElementJpeg(hostId, rect);
          } catch (err) {
            console.warn('YouTube moment image skipped', err);
          } finally {
            clean.destroy();
            host.remove();
            setCapturing(false);
          }
        }

        if (imageDataUrl) {
          lastImageKeyRef.current = key;
          onMomentRef.current?.(seconds, imageDataUrl);
        }
      } finally {
        resolveJob();
        if (captureJobRef.current === job) captureJobRef.current = null;
      }
    })();
    return job;
  };
  const captureMomentRef = useRef(captureMoment);
  captureMomentRef.current = captureMoment;

  const finish = () => {
    Promise.resolve(captureMoment()).finally(() => onClose?.());
  };

  useEffect(() => {
    lastImageKeyRef.current = '';
  }, [video?.videoId]);

  useEffect(() => {
    if (!open || !iframeRef.current) return undefined;
    const iframe = iframeRef.current;
    if (!iframe.id) iframe.id = `board-yt-${video.videoId}`;
    const widget = attachYouTubeWidget(iframe);
    widgetRef.current = widget;
    widget.onPause(time => {
      captureMomentRef.current?.(time);
    });
    return () => {
      widget.destroy();
      if (widgetRef.current === widget) widgetRef.current = null;
    };
  }, [open, src, video?.videoId]);

  return (
    <Dialog
      open={open}
      onClose={finish}
      maxWidth={false}
      className="board-youtube-dialog"
      PaperProps={{ className: 'board-youtube-paper', elevation: 0 }}
      BackdropProps={{ className: 'board-youtube-backdrop' }}
      aria-label={title}
    >
      {open ? (
        <div
          ref={frameRef}
          className={capturing ? 'board-youtube-frame is-capturing' : 'board-youtube-frame'}
        >
          <iframe
            ref={iframeRef}
            src={src}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          <IconButton
            className="board-youtube-save"
            onClick={() => { captureMoment(); }}
            title="Save this moment"
            aria-label="Save this moment"
            size="small"
          >
            <BookmarkBorderIcon fontSize="small" />
          </IconButton>
          <IconButton
            className="board-youtube-close"
            onClick={finish}
            title="Close"
            aria-label="Close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ) : null}
    </Dialog>
  );
};

export default YoutubeVideoModal;
